import { useAnimationControls } from "framer-motion";
import { DateTime } from "luxon";
import { useCallback, useState } from "react";
import type { RemovalDetails } from "../../../hooks/useHandleNurseChange";
import { MOTION } from "../../../theme/tokens";
import { AbsenceType } from "../../../utils/models";
import { useCallInReasonOptions } from "../../team/CallInReasons/callInReasonsHooks";
import { useRemovalReasonState } from "../hooks/useRemovalReasonState";

const SWING = MOTION.SHAKE_OFFSET_PX;
/** Framer Motion times transitions in seconds; the motion tokens are in ms. */
const MS_PER_SECOND = 1000;

/** The "pick a reason first" nudge: a short side-to-side shake (transform only), as the footer always did. */
const REASON_SHAKE = {
	x: [0, -SWING, SWING, -SWING, SWING, 0],
	transition: { duration: MOTION.DURATION.SHAKE / MS_PER_SECOND, ease: "easeInOut" as const, times: [0, 0.2, 0.4, 0.6, 0.8, 1] },
};

/**
 * What the manager records about the removal — one reason, note and call-in time for every shift picked. The
 * reason is required in every department (hospitals included) and nothing is pre-selected.
 */
export const useRemoveStaffDetails = () => {
	const reason = useRemovalReasonState({ hospitalRecordsCallIn: false });
	const { selectedReason, isReasonValid, getFullReason, removalMessage, handleReasonChange } = reason;
	const reasonOptions = useCallInReasonOptions();
	const [callInTime, setCallInTime] = useState<DateTime>(() => DateTime.now());
	const [showReasonError, setShowReasonError] = useState(false);
	const shakeControls = useAnimationControls();
	const isCallInReason = selectedReason === AbsenceType.CALL_IN;

	const changeReason = useCallback(
		(value: string | undefined) => {
			handleReasonChange(value);
			if (value) setShowReasonError(false);
		},
		[handleReasonChange],
	);

	/** True when the removal may go ahead; otherwise flags the reason field and shakes it. */
	const confirmReason = useCallback(async (): Promise<boolean> => {
		if (isReasonValid) return true;
		setShowReasonError(true);
		await shakeControls.start(REASON_SHAKE);
		return false;
	}, [isReasonValid, shakeControls]);

	const toRemovalDetails = useCallback(
		(): RemovalDetails => ({
			message: getFullReason(),
			// DEFAULT: an empty note field is no note — none is sent, rather than an empty string saved with the removal.
			removalMessage: removalMessage || undefined,
			callInTime: isCallInReason ? callInTime : undefined,
		}),
		[getFullReason, removalMessage, isCallInReason, callInTime],
	);

	return {
		...reason,
		changeReason,
		reasonOptions,
		isCallInReason,
		callInTime,
		setCallInTime,
		showReasonError,
		shakeControls,
		confirmReason,
		toRemovalDetails,
	};
};

export type RemoveStaffDetails = ReturnType<typeof useRemoveStaffDetails>;
